import api from "./axios";

/* ───────── Tipos ───────── */
export type EstadoCuadro = "borrador" | "publicado" | "archivado";
export type EstadoTurno = "borrador" | "publicado" | "cancelado";
export type EstadoAsig = "asignado" | "liberado";
export type TipoInc = "no_puedo_cubrir" | "otro";
export type EstadoInc = "abierta" | "resuelta";

export type Cuadro = {
  id: number;
  anio: number;
  mes: number; // 1..12
  estado: EstadoCuadro;
  nota?: string | null;
  publicadoPorId?: number | null;
  publicadoAt?: string | null; // ISO
  creadoPorId?: number | null;
  createdAt: string;
  updatedAt: string;
};

/* ===== Turnos ===== */
export type Turno = {
  id: number;
  cuadroId: number;
  fecha: string; // YYYY-MM-DD
  horaInicio: string; // HH:mm
  horaFin: string; // HH:mm
  formatoId: 1 | 2; // 1 Online, 2 Presencial
  centroId?: number | null;
  capacidad: number;
  estado: EstadoTurno;
  nota?: string | null;
  nombreCentro?: string | null;

  /** Nombres (o emails) de las personas asignadas al turno */
  asignados: string[];
};

export type Asignacion = {
  id: number;
  turnoId: number;
  usuarioId: number;
  estado: EstadoAsig;
  createdAt: string;
};

export type Incidencia = {
  id: number;
  turnoId: number;
  usuarioId: number;
  tipo: TipoInc;
  mensaje?: string | null;
  estado: EstadoInc;
  resueltaPorId?: number | null;
  resueltaAt?: string | null;
  createdAt: string;
};

export type Manager = { usuarioId: number; createdAt: string };

/* ───────── Helpers ───────── */
const toHHmm = (s?: string | null) => (s ? String(s).slice(0, 5) : "");

/** Calcula `asignados` desde payload plano o con relaciones */
function extractAsignados(p: any): string[] {
  // ① Si ya viene aplanado: array de strings o de objetos {id,nombreCompleto,email}
  if (Array.isArray(p?.asignados) && p.asignados.length) {
    return p.asignados
      .map((x: any) =>
        typeof x === "string"
          ? x
          : x?.nombreCompleto || x?.email || (x?.id != null ? `#${x.id}` : null)
      )
      .filter(Boolean);
  }
  // ② Si vienen relaciones: asignaciones[].usuario
  if (Array.isArray(p?.asignaciones)) {
    return p.asignaciones
      .filter((a: any) => a?.estado === "asignado")
      .map((a: any) => a?.usuario?.nombreCompleto || a?.usuario?.email || null)
      .filter(Boolean);
  }
  return [];
}

/* ───────── Normalizadores ───────── */
const normCuadro = (p: any): Cuadro => ({
  id: Number(p.id),
  anio: Number(p.anio),
  mes: Number(p.mes),
  estado: p.estado,
  nota: p.nota ?? null,
  publicadoPorId: p.publicadoPorId ?? p.publicado_por_id ?? null,
  publicadoAt: p.publicadoAt ?? p.publicado_at ?? null,
  creadoPorId: p.creadoPorId ?? p.creado_por_id ?? null,
  createdAt: p.createdAt ?? p.created_at,
  updatedAt: p.updatedAt ?? p.updated_at,
});

const normTurno = (p: any): Turno => ({
  id: Number(p.id),
  cuadroId: Number(p.cuadroId ?? p.cuadro_id),
  fecha: String(p.fecha),
  horaInicio: toHHmm(p.horaInicio ?? p.hora_inicio),
  horaFin: toHHmm(p.horaFin ?? p.hora_fin),
  formatoId: Number(p.formatoId ?? p.formato_id) as 1 | 2,
  centroId: p.centroId ?? p.centro_id ?? null,
  capacidad: Number(p.capacidad ?? 1),
  estado: p.estado as EstadoTurno,
  nota: p.nota ?? null,
  nombreCentro: p.nombreCentro ?? p.centroNombre ?? null,
  asignados: extractAsignados(p), // 👈 nombres listos para la UI
});

const normAsig = (p: any): Asignacion => ({
  id: Number(p.id),
  turnoId: Number(p.turnoId ?? p.turno_id),
  usuarioId: Number(p.usuarioId ?? p.usuario_id),
  estado: p.estado,
  createdAt: p.createdAt ?? p.created_at,
});

const normInc = (p: any): Incidencia => ({
  id: Number(p.id),
  turnoId: Number(p.turnoId ?? p.turno_id),
  usuarioId: Number(p.usuarioId ?? p.usuario_id),
  tipo: p.tipo,
  mensaje: p.mensaje ?? null,
  estado: p.estado,
  resueltaPorId: p.resueltaPorId ?? p.resuelta_por_id ?? null,
  resueltaAt: p.resueltaAt ?? p.resuelta_at ?? null,
  createdAt: p.createdAt ?? p.created_at,
});

/* ════════════════════════════════════════════════════════
   CUADROS
   ════════════════════════════════════════════════════════ */
export async function listCuadros(): Promise<Cuadro[]> {
  const { data } = await api.get("/guardias/cuadros");
  return (Array.isArray(data) ? data : []).map(normCuadro);
}
export async function getCuadro(id: number): Promise<Cuadro> {
  const { data } = await api.get(`/guardias/cuadros/${id}`);
  return normCuadro(data);
}
export async function createCuadro(dto: {
  anio: number;
  mes: number;
  nota?: string;
}): Promise<Cuadro> {
  const { data } = await api.post("/guardias/cuadros", dto);
  return normCuadro(data);
}
export async function updateCuadro(id: number, dto: Partial<{ nota: string }>): Promise<Cuadro> {
  const { data } = await api.put(`/guardias/cuadros/${id}`, dto);
  return normCuadro(data);
}
export async function publicarCuadro(id: number): Promise<Cuadro> {
  const { data } = await api.patch(`/guardias/cuadros/${id}/publicar`);
  return normCuadro(data);
}
export async function archivarCuadro(id: number): Promise<Cuadro> {
  const { data } = await api.patch(`/guardias/cuadros/${id}/archivar`);
  return normCuadro(data);
}

/* ════════════════════════════════════════════════════════
   TURNOS
   ════════════════════════════════════════════════════════ */
export async function listTurnos(cuadroId?: number): Promise<Turno[]> {
  const { data } = await api.get("/guardias/turnos", { params: { cuadroId } });
  return (Array.isArray(data) ? data : []).map(normTurno);
}
export async function getTurno(id: number): Promise<Turno> {
  const { data } = await api.get(`/guardias/turnos/${id}`);
  return normTurno(data);
}
export async function createTurno(dto: {
  cuadroId: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  formatoId: number;
  centroId?: number;
  capacidad?: number;
  nota?: string;
}): Promise<Turno> {
  const { data } = await api.post("/guardias/turnos", dto);
  return normTurno(data);
}
export async function updateTurno(id: number, dto: Partial<Turno>): Promise<Turno> {
  const { data } = await api.put(`/guardias/turnos/${id}`, dto);
  return normTurno(data);
}
export async function publicarTurno(id: number): Promise<Turno> {
  const { data } = await api.patch(`/guardias/turnos/${id}/publicar`);
  return normTurno(data);
}
export async function cancelarTurno(id: number): Promise<Turno> {
  const { data } = await api.patch(`/guardias/turnos/${id}/cancelar`);
  return normTurno(data);
}
export async function removeTurno(id: number): Promise<{ id: number; deleted: boolean }> {
  const { data } = await api.delete(`/guardias/turnos/${id}`);
  return data;
}

/* ════════════════════════════════════════════════════════
   ASIGNACIONES
   ════════════════════════════════════════════════════════ */
export async function listAsignaciones(turnoId: number): Promise<Asignacion[]> {
  const { data } = await api.get(`/guardias/turnos/${turnoId}/asignaciones`);
  return (Array.isArray(data) ? data : []).map(normAsig);
}
export async function addAsignacion(turnoId: number, usuarioId: number): Promise<Asignacion> {
  const { data } = await api.post(`/guardias/turnos/${turnoId}/asignaciones`, {
    usuarioId,
  });
  return normAsig(data);
}
export async function liberarAsignacion(asigId: number): Promise<Asignacion> {
  const { data } = await api.patch(`/guardias/asignaciones/${asigId}/liberar`);
  return normAsig(data);
}

/* ════════════════════════════════════════════════════════
   INCIDENCIAS
   ════════════════════════════════════════════════════════ */
export async function listIncidencias(
  params: { estado?: EstadoInc; turnoId?: number } = {}
): Promise<Incidencia[]> {
  const { data } = await api.get("/guardias/incidencias", { params });
  return (Array.isArray(data) ? data : []).map(normInc);
}
export async function crearIncidencia(
  turnoId: number,
  tipo: TipoInc = "no_puedo_cubrir",
  mensaje?: string
) {
  const { data } = await api.post("/guardias/incidencias", {
    turnoId,
    tipo,
    mensaje,
  });
  return normInc(data);
}
export async function resolverIncidencia(id: number) {
  const { data } = await api.patch(`/guardias/incidencias/${id}/resolver`);
  return normInc(data);
}

/* ════════════════════════════════════════════════════════
   MANAGERS
   ════════════════════════════════════════════════════════ */
export async function listManagers(): Promise<Manager[]> {
  const { data } = await api.get("/guardias/managers");
  return (Array.isArray(data) ? data : []) as Manager[];
}
export async function addManager(usuarioId: number) {
  const { data } = await api.post(`/guardias/managers/${usuarioId}`);
  return data;
}
export async function removeManager(usuarioId: number) {
  const { data } = await api.delete(`/guardias/managers/${usuarioId}`);
  return data;
}
